import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  ShieldCheck, Award, Download, MessageCircle, CheckCircle2, XCircle, AlertCircle, ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const estatusBadge = (v) => v === "Vigente"
  ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Vigente</Badge>
  : v === "Vencida"
  ? <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Vencida</Badge>
  : v === "Anulada"
  ? <Badge className="bg-slate-200 text-slate-700 line-through">Anulada</Badge>
  : <Badge>{v || "-"}</Badge>;

export default function PortalSolvenciaPage() {
  const { token, user } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const navigate = useNavigate();

  const [eleg, setEleg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emitiendo, setEmitiendo] = useState(false);
  const [misSolvencias, setMisSolvencias] = useState([]);

  const cargar = async () => {
    setLoading(true);
    try {
      const [e, l] = await Promise.all([
        axios.get(`${API}/solvencias/mi-elegibilidad`, auth),
        axios.get(`${API}/solvencias`, auth),
      ]);
      setEleg(e.data);
      setMisSolvencias(l.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar información");
    } finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []); // eslint-disable-line

  const emitir = async () => {
    setEmitiendo(true);
    try {
      const r = await axios.post(`${API}/solvencias/emitir`, {}, auth);
      toast.success(r.data?.created ? "Solvencia emitida ¡descargando!" : "Ya tienes una solvencia vigente. Descargando...");
      const sol = r.data?.solvencia;
      if (sol) await descargarPDF(sol.id, sol.nro_solvencia);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo emitir la solvencia");
    } finally { setEmitiendo(false); }
  };

  const descargarPDF = async (id, nro) => {
    const r = await axios.get(`${API}/solvencias/${id}/pdf`, { ...auth, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `solvencia_${(nro || "").replace(/\//g, "-")}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const compartirWhatsApp = async (s) => {
    try {
      const st = await axios.get(`${API}/solvencias/${s.id}/share-token`, auth);
      const shareToken = st.data?.share_token;
      const pdfUrl = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/public/solvencia/${s.id}/pdf?token=${shareToken}`;
      const msg =
        `📄 *Certificado de Solvencia · Aseo Urbano*\n\n` +
        `N° Solvencia: ${s.nro_solvencia}\n` +
        `Válida hasta: ${s.fecha_vencimiento}\n\n` +
        `Descargar: ${pdfUrl}\n\n` +
        `— Palma Plus, Saneamiento Urbano`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (e) { toast.error("No se pudo generar el enlace"); }
  };

  return (
    <div className="space-y-6" data-testid="portal-solvencia-page">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-orange-500" /> Mi Solvencia
        </h1>
        <p className="text-green-700/70 mt-1">
          Descargue su certificado de solvencia de aseo urbano si se encuentra al día con el mes en curso.
        </p>
      </div>

      {loading && (
        <Card className="p-10 text-center text-slate-500 border-green-100">Cargando información...</Card>
      )}

      {!loading && eleg && (
        <Card
          className="p-8 border-0 text-white shadow-xl"
          style={{
            background: eleg.elegible
              ? "linear-gradient(135deg,#14532D 0%,#166534 55%,#65A30D 140%)"
              : "linear-gradient(135deg,#7F1D1D 0%,#B91C1C 60%,#EA580C 140%)",
          }}
          data-testid="hero-solvencia"
        >
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="max-w-xl">
              <p className="text-orange-200 text-xs font-bold uppercase tracking-widest">
                Estatus para {eleg.periodo_actual_label}
              </p>
              <h2 className="font-display font-extrabold text-4xl mt-2 flex items-center gap-3">
                {eleg.elegible ? (
                  <><CheckCircle2 className="w-9 h-9 text-emerald-300" /> Está SOLVENTE</>
                ) : (
                  <><XCircle className="w-9 h-9 text-rose-200" /> NO SOLVENTE</>
                )}
              </h2>
              <p className="text-white/90 text-sm mt-3">
                {eleg.elegible
                  ? "Su pago del mes en curso ya fue validado. Puede emitir y descargar su certificado oficial de solvencia."
                  : eleg.razon || "Debe registrar y validar el pago del mes en curso antes de emitir la solvencia."}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              {eleg.elegible ? (
                <Button
                  onClick={emitir}
                  disabled={emitiendo}
                  size="lg"
                  className="bg-white text-green-900 hover:bg-orange-50 font-bold shadow-lg"
                  data-testid="btn-emitir-mi-solvencia"
                >
                  <Award className="w-5 h-5 mr-2" />
                  {emitiendo ? "Generando..." : "Descargar Solvencia"}
                </Button>
              ) : (
                <Button
                  onClick={() => navigate("/portal/reportar-pago")}
                  size="lg"
                  className="bg-white text-rose-800 hover:bg-orange-50 font-bold shadow-lg"
                  data-testid="btn-ir-reportar-pago"
                >
                  Reportar mi pago <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {!loading && (
        <Card className="p-4 border-green-100">
          <h3 className="font-display font-bold text-lg text-green-900 mb-3">Solvencias emitidas</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-green-50 text-green-900">
                <tr>
                  <th className="text-left p-2.5">N° Solvencia</th>
                  <th className="text-left p-2.5">Período</th>
                  <th className="text-left p-2.5">Emisión</th>
                  <th className="text-left p-2.5">Vencimiento</th>
                  <th className="text-center p-2.5">Estatus</th>
                  <th className="text-right p-2.5">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {misSolvencias.map(s => (
                  <tr key={s.id} className="border-b border-green-50">
                    <td className="p-2.5 font-mono">{s.nro_solvencia}</td>
                    <td className="p-2.5">{s.periodo_cubierto}</td>
                    <td className="p-2.5">{s.fecha_emision}</td>
                    <td className="p-2.5">{s.fecha_vencimiento}</td>
                    <td className="p-2.5 text-center">{estatusBadge(s.estatus)}</td>
                    <td className="p-2.5 text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => descargarPDF(s.id, s.nro_solvencia)} title="Descargar" data-testid={`portal-solv-download-${s.nro_solvencia}`}>
                        <Download className="w-4 h-4 text-green-700" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => compartirWhatsApp(s)} title="Compartir por WhatsApp" data-testid={`portal-solv-wa-${s.nro_solvencia}`}>
                        <MessageCircle className="w-4 h-4 text-emerald-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {!misSolvencias.length && (
                  <tr><td colSpan="6" className="p-8 text-center text-slate-400">
                    <AlertCircle className="w-5 h-5 inline mr-2 text-slate-400" />
                    Aún no has emitido solvencias.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
